// --- Core ---
import {useIntl} from "react-intl";
import {map} from "underscore";
// --- Material UI ---
import {FormControl, IconButton, Menu, MenuItem, Select} from "@mui/material";
import {Translate} from "@mui/icons-material";
// --- SaniQ ---
import {LocaleItemContent} from "shared/Locale";
import {Locale as LocaleType, localeDefinitions, safeLocale} from "src/config/i18n";
import {useCallback, useState} from "react";
import {switchLocale} from "../../app/reducers/App";
import {useAppDispatch} from "store";

interface LocaleProps {
  /**
   * Callback when a locale is selected.
   *
   * @param locale The selected locale. Corresponds to the language part of a POSIX locale
   */
  onLocaleChange: (locale: LocaleType) => void;
}

/**
 * Drop down menu to select one of the supported locales.
 *
 * Currently includes:
 *   - German
 *   - English
 *   - Automatic Translations through DeepL
 *     - Finnish
 *     - French
 *     - Greek
 *     - Italian
 *     - Portuguese
 *     - Spanish
 *     - Russian
 *
 * @example
 * <Locale
 *   onLocaleChange={(locale) => {
 *     dispatch(switchLocale(locale));
 *   }}
 * />
 *
 * @see switchLocale
 */
const Locale = ({onLocaleChange}: LocaleProps) => {
  const intl = useIntl();

  return (
    <FormControl size="small">
      <Select
        value={intl.locale}
        sx={{mr: 2}}
        onChange={
          (event) => {
            onLocaleChange(safeLocale(event.target.value));
          }
        }
        SelectDisplayProps={{style: {display: "flex", alignItems: "center"}}}
      >
        {
          map(localeDefinitions, definition => (
            <MenuItem value={definition.key} key={definition.key}>
              <LocaleItemContent definition={definition}/>
            </MenuItem>
          ))
        }
      </Select>
    </FormControl>
  );
};

const LocaleIconButton = () => {
  const dispatch = useAppDispatch();
  const [anchor, setAnchor] = useState(null);

  const handleClick = useCallback((event) => {
    dispatch(switchLocale(event.currentTarget.dataset.value));
    setAnchor(null);
  }, [dispatch]);

  return (
    <>
      <IconButton
        onClick={
          (event) => {
            setAnchor(event.currentTarget);
          }
        }
      >
        <Translate/>
      </IconButton>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={
          () => {
            setAnchor(null);
          }
        }
      >
        {
          map(localeDefinitions, definition => (
            <MenuItem
              key={definition.key}
              data-value={definition.key}
              onClick={handleClick}
            >
              <LocaleItemContent definition={definition}/>
            </MenuItem>
          ))
        }
      </Menu>
    </>
  );
};

export {
  LocaleIconButton
};

export default Locale;
